/**
 * Checklist Domain Logic
 *
 * Centralized business logic for filtering and querying checklists.
 * UI components should use these functions instead of implementing filtering logic inline.
 */
import { CHECKLIST_STATUS } from './status.js';
/**
 * Checks if a checklist is the reconciled consensus for its cell
 * @param checklist - The checklist object
 * @returns True if the checklist is a reconciled checklist
 */
export function isReconciledChecklist(checklist) {
    if (!checklist)
        return false;
    return checklist.kind === 'consensus';
}
/**
 * Gets checklists for the todo tab (assigned to user, not finalized or reviewer-completed)
 * @param study - The study object
 * @param userId - The current user ID
 * @returns Array of checklists for todo tab
 */
export function getTodoChecklists(study, userId) {
    if (!study || !userId)
        return [];
    const checklists = study.checklists || [];
    return checklists.filter(c => c.assignedTo === userId &&
        c.status !== CHECKLIST_STATUS.FINALIZED &&
        c.status !== CHECKLIST_STATUS.REVIEWER_COMPLETED);
}
/**
 * Determines if a study should appear in a specific tab
 * @param study - The study object
 * @param tab - The tab name ('todo', 'reconcile', 'completed')
 * @param userId - The current user ID (required for 'todo' tab)
 * @returns True if study should appear in the tab
 */
export function shouldShowInTab(study, tab, userId) {
    if (!study)
        return false;
    switch (tab) {
        case 'todo': {
            if (!userId)
                return false;
            // Must be assigned to user
            if (study.reviewer1 !== userId && study.reviewer2 !== userId)
                return false;
            const checklists = study.checklists || [];
            const userChecklists = checklists.filter(c => c.assignedTo === userId);
            // Show if user has no checklist yet OR has a non-finalized/reviewer-completed checklist
            return (userChecklists.length === 0 ||
                userChecklists.some(c => c.status !== CHECKLIST_STATUS.FINALIZED &&
                    c.status !== CHECKLIST_STATUS.REVIEWER_COMPLETED));
        }
        case 'reconcile': {
            // Must have both reviewers assigned (single reviewer studies never go to reconcile tab)
            if (!study.reviewer1 || !study.reviewer2)
                return false;
            const checklists = study.checklists || [];
            const awaitingReconcile = checklists.filter(c => !isReconciledChecklist(c) && c.status === CHECKLIST_STATUS.REVIEWER_COMPLETED);
            if (awaitingReconcile.length === 0)
                return false;
            // Group by outcomeId (for ROB2/ROBINS_I) or by type (for AMSTAR2)
            const groups = new Map();
            for (const checklist of awaitingReconcile) {
                const groupKey = checklist.outcomeId || `type:${checklist.type}`;
                if (!groups.has(groupKey)) {
                    groups.set(groupKey, {
                        checklists: [],
                        outcomeId: checklist.outcomeId ?? null,
                        type: checklist.type,
                    });
                }
                groups.get(groupKey).checklists.push(checklist);
            }
            // Check if any group has a pair ready for reconciliation
            // AND doesn't already have a finalized reconciled checklist for that outcome
            for (const group of groups.values()) {
                if (group.checklists.length === 2) {
                    const hasFinalized = checklists.some(c => isReconciledChecklist(c) &&
                        c.status === CHECKLIST_STATUS.FINALIZED &&
                        c.type === group.type &&
                        (c.outcomeId ?? null) === group.outcomeId);
                    if (!hasFinalized)
                        return true;
                }
            }
            // Also show if any group has 1 checklist waiting (waiting for second reviewer)
            for (const group of groups.values()) {
                if (group.checklists.length === 1) {
                    return true;
                }
            }
            return false;
        }
        case 'completed': {
            const checklists = study.checklists || [];
            return checklists.some(c => c.status === CHECKLIST_STATUS.FINALIZED);
        }
        default:
            return false;
    }
}
/**
 * Gets filtered studies for a specific tab
 * @param studies - Array of study objects
 * @param tab - The tab name ('todo', 'reconcile', 'completed')
 * @param userId - The current user ID (required for 'todo' tab)
 * @returns Filtered array of studies
 */
export function getStudiesForTab(studies, tab, userId) {
    if (!studies || !Array.isArray(studies))
        return [];
    if (tab === 'todo') {
        // For todo tab, also transform studies to include filtered checklists
        return studies
            .filter(study => shouldShowInTab(study, tab, userId))
            .map(study => {
            const originalChecklists = study.checklists || [];
            const todoChecklists = getTodoChecklists(study, userId);
            const userHasChecklist = originalChecklists.some(c => c.assignedTo === userId);
            return {
                ...study,
                checklists: todoChecklists,
                _needsChecklist: !userHasChecklist,
            };
        })
            .filter(study => (study.checklists?.length ?? 0) > 0 || study._needsChecklist);
    }
    // For other tabs, just filter studies
    return studies.filter(study => shouldShowInTab(study, tab, userId));
}
/**
 * Gets the count of studies/checklists for a tab badge
 * @param studies - Array of study objects
 * @param tab - The tab name ('todo', 'reconcile', 'completed')
 * @param userId - The current user ID (required for 'todo' tab)
 * @returns Count for the tab badge
 */
export function getChecklistCount(studies, tab, userId) {
    if (!studies || !Array.isArray(studies))
        return 0;
    return getStudiesForTab(studies, tab, userId).length;
}
/**
 * Determines the next status when a reviewer marks their checklist as complete
 * @param study - The study object
 * @returns The status to set (FINALIZED for single reviewer, REVIEWER_COMPLETED for dual reviewer)
 */
export function getNextStatusForCompletion(study) {
    if (!study)
        return CHECKLIST_STATUS.FINALIZED;
    const isSingleReviewer = study.reviewer1 && !study.reviewer2;
    if (isSingleReviewer) {
        // Single reviewer: goes directly to finalized
        return CHECKLIST_STATUS.FINALIZED;
    }
    // Dual reviewer: goes to reviewer-completed (awaiting reconciliation)
    return CHECKLIST_STATUS.REVIEWER_COMPLETED;
}
/**
 * Gets all reconciled checklists for a study that are not yet finalized
 * @param study - The study object
 * @returns Array of in-progress or reconciling checklists
 */
export function getInProgressReconciledChecklists(study) {
    if (!study || !study.checklists)
        return [];
    return study.checklists.filter(c => isReconciledChecklist(c) && c.status !== CHECKLIST_STATUS.FINALIZED);
}
/**
 * Determines if a study has dual reviewers
 * @param study - The study object
 * @returns True if study has both reviewer1 and reviewer2
 */
export function isDualReviewerStudy(study) {
    if (!study)
        return false;
    return !!(study.reviewer1 && study.reviewer2);
}
/**
 * Gets the original reviewer checklists that were reconciled
 * @param study - The study object
 * @param reconciliationProgress - Reconciliation progress data with checklist1Id and checklist2Id
 * @returns Array of original reviewer checklists (metadata only)
 */
export function getOriginalReviewerChecklists(study, reconciliationProgress) {
    if (!study || !study.checklists || !reconciliationProgress)
        return [];
    const { checklist1Id, checklist2Id } = reconciliationProgress;
    if (!checklist1Id || !checklist2Id)
        return [];
    const checklists = study.checklists || [];
    const checklist1 = checklists.find(c => c.id === checklist1Id);
    const checklist2 = checklists.find(c => c.id === checklist2Id);
    const result = [];
    if (checklist1)
        result.push(checklist1);
    if (checklist2)
        result.push(checklist2);
    return result;
}
/**
 * Groups reconciliation-eligible checklists by outcome (or by type for AMSTAR2)
 */
export function getReconciliationChecklistsByOutcome(study) {
    if (!study)
        return [];
    const checklists = study.checklists || [];
    const awaitingReconcile = checklists.filter(c => !isReconciledChecklist(c) && c.status === CHECKLIST_STATUS.REVIEWER_COMPLETED);
    const groups = new Map();
    for (const checklist of awaitingReconcile) {
        const groupKey = checklist.outcomeId || `type:${checklist.type}`;
        if (!groups.has(groupKey)) {
            groups.set(groupKey, {
                outcomeId: checklist.outcomeId || null,
                type: checklist.type,
                checklists: [],
            });
        }
        groups.get(groupKey).checklists.push(checklist);
    }
    return Array.from(groups.values());
}
/**
 * Gets reconciliation pairs that are ready (have exactly 2 checklists)
 */
export function getReadyReconciliationPairs(study) {
    const groups = getReconciliationChecklistsByOutcome(study);
    return groups.filter(g => g.checklists.length === 2);
}
/**
 * Finds a reconciled checklist for a specific outcome
 */
export function findReconciledChecklistForOutcome(study, outcomeId, type, excludeId = null) {
    if (!study || !study.checklists)
        return null;
    return (study.checklists.find(c => {
        if (!isReconciledChecklist(c))
            return false;
        if (excludeId && c.id === excludeId)
            return false;
        if (c.type !== type)
            return false;
        return c.outcomeId === outcomeId;
    }) || null);
}
/**
 * The key of one (instrument, outcome) cell within a study. The instrument is
 * part of it because one outcome may be appraised with more than one
 * instrument, and each needs its own plan row, reconciliation and grouping.
 */
export function getOutcomeKey(outcomeId, type) {
    return outcomeId ? `${type}:${outcomeId}` : `type:${type}`;
}
/**
 * Checks whether a finalized reconciliation can be reopened for an outcome.
 *
 * Reopening only applies to dual-reviewer studies and requires the original
 * reviewer-completed pair to still exist; otherwise un-finalizing the
 * reconciled checklist would leave the study visible in neither the
 * reconcile tab nor the completed tab.
 *
 * @param study - The study object
 * @param outcomeId - The outcome ID (null for AMSTAR2)
 * @param type - The checklist type
 * @returns The finalized reconciled checklist to reopen, or null if not allowed
 */
export function getReopenableReconciledChecklist(study, outcomeId, type) {
    if (!study || !study.reviewer1 || !study.reviewer2)
        return null;
    const checklists = study.checklists || [];
    const finalizedReconciled = checklists.find(c => isReconciledChecklist(c) &&
        c.status === CHECKLIST_STATUS.FINALIZED &&
        c.type === type &&
        (c.outcomeId ?? null) === outcomeId);
    if (!finalizedReconciled)
        return null;
    const reviewerPair = checklists.filter(c => !isReconciledChecklist(c) &&
        c.status === CHECKLIST_STATUS.REVIEWER_COMPLETED &&
        c.type === type &&
        (c.outcomeId ?? null) === outcomeId);
    if (reviewerPair.length !== 2)
        return null;
    return finalizedReconciled;
}
/**
 * Builds the plan for sending an outcome group back to the todo phase, or
 * returns null when the action does not apply.
 *
 * Mirrors the `checklist.sendBackToTodo` mutator's guards so the UI only
 * offers the action when the server would accept it: a group whose
 * reconciliation is already finalized must be reopened from the Completed tab
 * instead.
 *
 * @param study - The study object
 * @param outcomeId - The outcome ID (null for AMSTAR2)
 * @param type - The checklist type
 * @returns The plan, or null if the group cannot be sent back
 */
export function getSendBackToTodoPlan(study, outcomeId, type) {
    if (!study)
        return null;
    const checklists = study.checklists || [];
    const inGroup = checklists.filter(c => c.type === type && (c.outcomeId ?? null) === outcomeId);
    const reviewerChecklists = inGroup.filter(c => !isReconciledChecklist(c) && c.status === CHECKLIST_STATUS.REVIEWER_COMPLETED);
    if (reviewerChecklists.length === 0)
        return null;
    const reconciledChecklists = inGroup.filter(isReconciledChecklist);
    if (reconciledChecklists.some(c => c.status === CHECKLIST_STATUS.FINALIZED))
        return null;
    return { reviewerChecklists, reconciledChecklist: reconciledChecklists[0] ?? null };
}
/**
 * Every cell of the study's appraisal plan with the checklists it holds, plus
 * any cell that holds checklists outside the plan (work from before the plan
 * existed). Lets a listing show a planned cell instead of a blank.
 */
export function getAppraisalCells(study) {
    if (!study)
        return [];
    const cells = new Map();
    const cellFor = (type, outcomeId) => {
        const key = getOutcomeKey(outcomeId, type);
        let cell = cells.get(key);
        if (!cell) {
            cell = { outcomeId: outcomeId || null, type, checklists: [], planned: true };
            cells.set(key, cell);
        }
        return cell;
    };
    for (const plan of study.appraisals || [])
        cellFor(plan.type, plan.outcomeId);
    for (const checklist of study.checklists || []) {
        const cell = cellFor(checklist.type, checklist.outcomeId);
        cell.checklists.push(checklist);
        cell.planned = false;
    }
    return Array.from(cells.values());
}
/**
 * The in-progress checklists a slot change would take away from reviewers who
 * leave the study. Mirrors the `study.assignReviewers` guard so the UI can ask
 * whether to hand them over or discard them before sending the mutation.
 * Pending checklists move or vanish silently; completed ones are never touched.
 */
export function getInProgressChecklistsOfLeavingReviewers(study, next) {
    if (!study)
        return [];
    const before = [study.reviewer1 ?? null, study.reviewer2 ?? null];
    const after = [
        next.reviewer1 === undefined ? before[0] : next.reviewer1,
        next.reviewer2 === undefined ? before[1] : next.reviewer2,
    ];
    const leavers = before.filter(u => u && !after.includes(u));
    if (leavers.length === 0)
        return [];
    return (study.checklists || []).filter(c => !isReconciledChecklist(c) &&
        c.status === CHECKLIST_STATUS.IN_PROGRESS &&
        leavers.includes(c.assignedTo ?? null));
}
/**
 * Groups completed checklists by outcome
 */
export function getCompletedChecklistsByOutcome(study) {
    if (!study)
        return [];
    const checklists = study.checklists || [];
    const completed = checklists.filter(c => c.status === CHECKLIST_STATUS.FINALIZED);
    if (completed.length === 0)
        return [];
    const groups = new Map();
    for (const checklist of completed) {
        const groupKey = getOutcomeKey(checklist.outcomeId, checklist.type);
        if (!groups.has(groupKey)) {
            groups.set(groupKey, {
                outcomeId: checklist.outcomeId || null,
                type: checklist.type,
                checklists: [],
            });
        }
        groups.get(groupKey).checklists.push(checklist);
    }
    return Array.from(groups.values());
}
//# sourceMappingURL=domain.js.map